import { styled } from 'baseui'
import { FLOATING_GAP } from '@/shared/theme/styled-components'
import { RAIL_MOTION_MS, RAIL_OVERLAY_Z_INDEX } from '@/shared/theme/railSlot'
import type { HopTimusPanelMode } from '../rightPanelContext'

const DOCK_WIDTH_REM = 24

export type HopTimusOpenLayout = Exclude<HopTimusPanelMode, 'closed'>

const DOCK_MOTION = { duration: '300ms', easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }

const AFTER_FOLD_DELAY_MS = RAIL_MOTION_MS

const dockOpening = {
  from: { width: '0' },
  to: { width: `${DOCK_WIDTH_REM}rem` },
}

const dockClosing = {
  from: { width: `${DOCK_WIDTH_REM}rem` },
  to: { width: '0' },
}

const floatingOpening = {
  from: { opacity: 0, transform: 'translateY(1.25rem)' },
  to: { opacity: 1, transform: 'none' },
}

const floatingClosing = {
  from: { opacity: 1, transform: 'none' },
  to: { opacity: 0, transform: 'translateY(1.25rem)' },
}

const fadeIn = { from: { opacity: 0 }, to: { opacity: 1 } }

const fadeOut = { from: { opacity: 1 }, to: { opacity: 0 } }

const frameAnimation = (layout: HopTimusOpenLayout, closing: boolean) => {
  if (layout === 'floating') return { name: closing ? floatingClosing : floatingOpening, duration: '250ms' }
  return { name: closing ? dockClosing : dockOpening, duration: DOCK_MOTION.duration }
}

export const HopTimusDockFrame = styled<
  'div',
  { $mode: HopTimusPanelMode; $closingFrom: HopTimusOpenLayout | null; $afterFold: boolean }
>('div', ({ $theme, $mode, $closingFrom, $afterFold }) => {
  const closing = $mode === 'closed'
  const layout = closing ? $closingFrom : $mode
  const animation = layout ? frameAnimation(layout, closing) : null
  const waitsForFold = !closing && layout === 'docked' && $afterFold

  return {
    boxSizing: 'border-box',
    overflow: 'hidden',
    backgroundColor: $theme.colors.white,
    display: layout ? 'flex' : 'none',
    ...(animation
      ? {
          animationName: animation.name,
          animationDuration: animation.duration,
          animationTimingFunction: DOCK_MOTION.easing,
          animationDelay: waitsForFold ? `${AFTER_FOLD_DELAY_MS}ms` : '0ms',
          animationFillMode: 'both',
        }
      : {}),
    ...(closing ? { pointerEvents: 'none' } : {}),
    '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
    ...(layout === 'floating'
      ? {
          position: 'fixed',
          right: '1rem',
          bottom: '1rem',
          zIndex: RAIL_OVERLAY_Z_INDEX,
          width: '26.25rem',
          height: '42.5rem',
          maxWidth: 'calc(100vw - 2rem)',
          maxHeight: 'calc(100vh - 2rem)',
          borderRadius: '1rem',
          boxShadow: $theme.lighting.shadow600,
        }
      : {
          position: 'sticky',
          top: FLOATING_GAP,
          flexGrow: 0,
          flexShrink: 0,
          width: `${DOCK_WIDTH_REM}rem`,
          height: `calc(100vh - 2 * ${FLOATING_GAP})`,
          borderRadius: '0.75rem',
        }),
  }
})

export const DockContent = styled<
  'div',
  { $layout: HopTimusOpenLayout | null; $closing: boolean; $afterFold: boolean }
>('div', ({ $layout, $closing, $afterFold }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  width: $layout === 'floating' ? '100%' : `${DOCK_WIDTH_REM}rem`,
  height: '100%',
  ...($layout === 'docked'
    ? {
        animationName: $closing ? fadeOut : fadeIn,
        animationDuration: $closing ? '150ms' : '200ms',
        animationDelay: $closing ? '0ms' : `${150 + ($afterFold ? AFTER_FOLD_DELAY_MS : 0)}ms`,
        animationTimingFunction: 'ease-out',
        animationFillMode: 'both',
      }
    : {}),
  '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
}))

export const PulseFrame = styled('iframe', {
  flexGrow: 1,
  width: '100%',
  height: '100%',
  border: 0,
})

// TODO: drop once PULSE draws a detach button in its own header; until then it sits over that header.
export const DockSwitchButton = styled('button', ({ $theme }) => ({
  position: 'absolute',
  top: '1rem',
  right: '5.25rem',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '2rem',
  height: '2rem',
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  border: 0,
  borderRadius: '999px',
  backgroundColor: $theme.colors.white,
  color: $theme.colors.contentSecondary,
  cursor: 'pointer',
  ':hover': { backgroundColor: $theme.colors.bg050, color: $theme.colors.primary },
  ':focus-visible': { outline: `2px solid ${$theme.colors.primary}`, outlineOffset: '2px' },
}))

export const DockMessage = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.75rem',
  height: '100%',
  paddingTop: '2rem',
  paddingBottom: '2rem',
  paddingLeft: '2rem',
  paddingRight: '2rem',
  textAlign: 'center',
  color: $theme.colors.contentSecondary,
  ...$theme.typography.LabelMedium,
}))

export const DockAvatar = styled('img', ({ $theme }) => ({
  width: '3.25rem',
  height: '3.25rem',
  borderRadius: '999px',
  objectFit: 'cover',
  boxShadow: `0 0 0 2px ${$theme.colors.primary}`,
}))
